import { useEffect, useMemo, useRef, useState } from 'react'

import { CalendarAppointmentBlock } from '@/components/calendar/CalendarAppointmentBlock'
import { layoutAppointmentLanes } from '@/lib/calendar/appointmentLayout'
import {
  PX_PER_MINUTE,
  buildCalendarGridLines,
  buildReschedulePayload,
  dropOffsetToMinutes,
  formatBookedDuration,
  formatMinutesLabel,
  parseTimeToMinutes,
} from '@/lib/calendar/dayCalendar'
import { todayIso } from '@/lib/calendar/calendarDates'
import { cn } from '@/lib/utils'

const UNASSIGNED_KEY = 'unassigned'

/**
 * @param {string} name
 */
function initials(name) {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (parts.length === 0) {
    return '?'
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

/**
 * @param {import('@/types/api').Appointment[]} appointments
 */
function bookedMinutesForColumn(appointments) {
  return appointments
    .filter((appointment) => !['cancelled', 'no_show'].includes(appointment.status))
    .reduce((sum, appointment) => sum + (appointment.duration_minutes ?? 30), 0)
}

/**
 * @param {import('@/types/api').Stylist[]} stylists
 * @param {import('@/types/api').Appointment[]} appointments
 * @param {number[] | null | undefined} visibleStylistIds
 */
function buildColumns(stylists, appointments, visibleStylistIds) {
  const filtered = stylists.filter((stylist) => {
    if (!stylist.is_active) {
      return false
    }

    if (!visibleStylistIds || visibleStylistIds.length === 0) {
      return true
    }

    return visibleStylistIds.includes(stylist.id)
  })

  const columns = filtered.map((stylist) => ({
    key: String(stylist.id),
    stylistId: stylist.id,
    label: stylist.name,
    colour: stylist.colour,
    specialty: stylist.skills?.[0] ?? 'Stylist',
  }))

  const hasUnassigned = appointments.some(
    (appointment) =>
      !appointment.stylist_id ||
      !stylists.some((stylist) => stylist.id === appointment.stylist_id),
  )

  if (hasUnassigned) {
    columns.push({
      key: UNASSIGNED_KEY,
      stylistId: null,
      label: 'Unassigned',
      colour: '#94a3b8',
      specialty: 'Needs assignment',
    })
  }

  return columns
}

/**
 * @param {import('@/types/api').Appointment} appointment
 * @param {Set<number>} stylistIds
 */
function columnKeyForAppointment(appointment, stylistIds) {
  if (!appointment.stylist_id || !stylistIds.has(appointment.stylist_id)) {
    return UNASSIGNED_KEY
  }

  return String(appointment.stylist_id)
}

/**
 * @param {{
 *   date: string
 *   appointments: import('@/types/api').Appointment[]
 *   stylists: import('@/types/api').Stylist[]
 *   openTime: string
 *   closeTime: string
 *   slotMinutes: number
 *   onReschedule: (appointmentId: number, payload: { starts_at: string, stylist_id?: number | null }) => Promise<void>
 *   onSelectAppointment?: (appointment: import('@/types/api').Appointment) => void
 *   onCreateAppointment?: () => void
 *   scheduleUnavailable?: boolean
 *   scheduleEmpty?: boolean
 *   rescheduleEnabled?: boolean
 *   reschedulingId?: number | null
 *   visibleStylistIds?: number[] | null
 *   scrollToTime?: string | null
 * }} props
 */
export function DayCalendar({
  date,
  appointments,
  stylists,
  openTime,
  closeTime,
  slotMinutes,
  onReschedule,
  onSelectAppointment,
  onCreateAppointment,
  scheduleUnavailable = false,
  scheduleEmpty = false,
  rescheduleEnabled = false,
  reschedulingId = null,
  visibleStylistIds = null,
  scrollToTime = null,
}) {
  const [draggingId, setDraggingId] = useState(null)
  const [nowMinutes, setNowMinutes] = useState(() => {
    const now = new Date()
    return now.getHours() * 60 + now.getMinutes()
  })
  const columnRefs = useRef({})
  const scrollRef = useRef(null)
  const didAutoScroll = useRef(false)

  const dayStartMinutes = parseTimeToMinutes(openTime)
  const dayEndMinutes = parseTimeToMinutes(closeTime)
  const pxPerMinute = PX_PER_MINUTE
  const gridHeight = (dayEndMinutes - dayStartMinutes) * pxPerMinute
  const gridLines = useMemo(
    () => buildCalendarGridLines(dayStartMinutes, dayEndMinutes),
    [dayEndMinutes, dayStartMinutes],
  )
  const isToday = date === todayIso()

  useEffect(() => {
    if (!isToday) {
      return undefined
    }

    const tick = () => {
      const now = new Date()
      setNowMinutes(now.getHours() * 60 + now.getMinutes())
    }

    tick()
    const timer = window.setInterval(tick, 30000)
    return () => window.clearInterval(timer)
  }, [isToday])

  useEffect(() => {
    didAutoScroll.current = false
  }, [date])

  useEffect(() => {
    if (!scrollRef.current) {
      return
    }

    if (scrollToTime) {
      const focusMinutes = parseTimeToMinutes(scrollToTime)
      if (focusMinutes < dayStartMinutes || focusMinutes > dayEndMinutes) {
        return
      }
      scrollRef.current.scrollTop = Math.max(
        0,
        (focusMinutes - dayStartMinutes) * pxPerMinute - 140,
      )
      didAutoScroll.current = true
      return
    }

    if (!isToday || didAutoScroll.current) {
      return
    }

    if (nowMinutes < dayStartMinutes || nowMinutes > dayEndMinutes) {
      return
    }

    const target = Math.max(0, (nowMinutes - dayStartMinutes) * pxPerMinute - 140)
    scrollRef.current.scrollTop = target
    didAutoScroll.current = true
  }, [
    dayEndMinutes,
    dayStartMinutes,
    isToday,
    nowMinutes,
    pxPerMinute,
    scrollToTime,
  ])

  const columns = useMemo(
    () => buildColumns(stylists, appointments, visibleStylistIds),
    [appointments, stylists, visibleStylistIds],
  )
  const stylistIds = useMemo(() => new Set(stylists.map((stylist) => stylist.id)), [stylists])

  const appointmentsByColumn = useMemo(() => {
    const grouped = Object.fromEntries(columns.map((column) => [column.key, []]))

    for (const appointment of appointments) {
      const key = columnKeyForAppointment(appointment, stylistIds)
      if (!grouped[key]) {
        continue
      }
      grouped[key].push(appointment)
    }

    return grouped
  }, [appointments, columns, stylistIds])

  const laneLayouts = useMemo(() => {
    /** @type {Record<string, Map<number, { column: number, totalColumns: number }>>} */
    const layouts = {}

    for (const column of columns) {
      layouts[column.key] = layoutAppointmentLanes(appointmentsByColumn[column.key] ?? [])
    }

    return layouts
  }, [appointmentsByColumn, columns])

  const showNowLine =
    isToday && nowMinutes >= dayStartMinutes && nowMinutes <= dayEndMinutes
  const nowTop = (nowMinutes - dayStartMinutes) * pxPerMinute

  /**
   * @param {import('@/types/api').Appointment} appointment
   * @param {number | null} targetStylistId
   * @param {DragEvent} event
   * @param {string} columnKey
   */
  async function handleDrop(appointment, targetStylistId, event, columnKey) {
    event.preventDefault()

    const columnElement = columnRefs.current[columnKey]
    if (!columnElement) {
      return
    }

    const offsetY =
      event.clientY - columnElement.getBoundingClientRect().top + columnElement.scrollTop
    const dropMinutes = dropOffsetToMinutes(offsetY, dayStartMinutes, pxPerMinute, slotMinutes)
    const maxStart = dayEndMinutes - (appointment.duration_minutes ?? slotMinutes)

    if (dropMinutes < dayStartMinutes || dropMinutes > maxStart) {
      setDraggingId(null)
      return
    }

    const payload = buildReschedulePayload(appointment, targetStylistId, date, dropMinutes)

    try {
      await onReschedule(appointment.id, payload)
    } finally {
      setDraggingId(null)
    }
  }

  if (columns.length === 0) {
    return (
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <p className="text-sm font-medium text-foreground">No stylists to show</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          Add active staff or clear stylist filters to display the schedule.
        </p>
      </div>
    )
  }

  const columnMin = 188

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-[hsl(var(--background))]">
      {!scheduleUnavailable && scheduleEmpty ? (
        <div className="mx-4 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/50 bg-card/70 px-4 py-2.5 text-sm">
          <div>
            <p className="font-medium text-foreground">No appointments scheduled</p>
            <p className="text-[12px] text-muted-foreground">Your schedule is clear for this day.</p>
          </div>
          {onCreateAppointment ? (
            <button
              type="button"
              onClick={onCreateAppointment}
              className="text-[12px] font-semibold text-primary hover:underline"
            >
              + Book appointment
            </button>
          ) : null}
        </div>
      ) : null}

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
        <div className="min-w-max pb-8">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `56px repeat(${columns.length}, minmax(${columnMin}px, 1fr))`,
            }}
          >
            <div className="sticky left-0 top-0 z-30 border-b border-r border-border/40 bg-[hsl(var(--background)/0.92)] px-2 py-3 backdrop-blur-md">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
                Time
              </span>
            </div>

            {columns.map((column) => {
              const columnAppointments = (appointmentsByColumn[column.key] ?? []).filter(
                (appointment) => !['cancelled', 'no_show'].includes(appointment.status),
              )
              const booked = bookedMinutesForColumn(columnAppointments)
              const capacity = Math.max(dayEndMinutes - dayStartMinutes, 1)
              const util = Math.min(100, Math.round((booked / capacity) * 100))
              const bookedLabel = formatBookedDuration(booked)

              return (
                <div
                  key={column.key}
                  className="sticky top-0 z-20 border-b border-r border-border/35 bg-[hsl(var(--background)/0.92)] px-3 py-2.5 backdrop-blur-md"
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white shadow-sm"
                      style={{ backgroundColor: column.colour ?? 'hsl(var(--primary))' }}
                      aria-hidden
                    >
                      {initials(column.label)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold tracking-tight text-foreground calendar-staff-column">
                        {column.label}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">{column.specialty}</p>
                      <p className="mt-1 truncate text-[11px] text-muted-foreground/90">
                        {columnAppointments.length === 0
                          ? 'Available today'
                          : `${columnAppointments.length} booking${columnAppointments.length === 1 ? '' : 's'}${bookedLabel ? ` · ${bookedLabel}` : ''}`}
                      </p>
                      {columnAppointments.length > 0 ? (
                        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted/80">
                          <div
                            className="h-full rounded-full bg-foreground/25"
                            style={{ width: `${Math.max(util, 4)}%` }}
                            aria-hidden
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}

            <div className="sticky left-0 z-20 border-r border-border/40 bg-[hsl(var(--background)/0.85)]">
              <div className="relative" style={{ height: gridHeight }}>
                {gridLines.map((line) =>
                  line.showLabel ? (
                    <div
                      key={line.minutes}
                      className="absolute left-0 right-0 px-1.5 text-[10px] tabular-nums calendar-time-label"
                      style={{ top: (line.minutes - dayStartMinutes) * pxPerMinute }}
                    >
                      <span
                        className={cn(
                          'relative -top-2 pr-1',
                          line.strength === 'hour'
                            ? 'font-semibold text-foreground/70'
                            : 'font-medium text-muted-foreground/70',
                        )}
                      >
                        {formatMinutesLabel(line.minutes)}
                      </span>
                    </div>
                  ) : null,
                )}
              </div>
            </div>

            {columns.map((column, columnIndex) => (
              <div
                key={`${column.key}-body`}
                ref={(element) => {
                  columnRefs.current[column.key] = element
                }}
                className={cn(
                  'relative border-r border-border/30',
                  draggingId && rescheduleEnabled && 'bg-primary/[0.02]',
                )}
                style={{ height: gridHeight }}
                onDragOver={(event) => {
                  if (!rescheduleEnabled) {
                    return
                  }
                  event.preventDefault()
                  event.dataTransfer.dropEffect = 'move'
                }}
                onDrop={(event) => {
                  if (!rescheduleEnabled) {
                    return
                  }
                  const appointment = appointments.find((item) => item.id === draggingId)
                  if (!appointment) {
                    return
                  }
                  handleDrop(appointment, column.stylistId, event, column.key)
                }}
              >
                {gridLines.map((line) => (
                  <div
                    key={`${column.key}-${line.minutes}`}
                    className={cn(
                      'pointer-events-none absolute left-0 right-0',
                      line.strength === 'hour' && 'border-t border-border/55',
                      line.strength === 'half' && 'border-t border-border/30',
                      line.strength === 'quarter' && 'border-t border-dashed border-border/15',
                    )}
                    style={{ top: (line.minutes - dayStartMinutes) * pxPerMinute }}
                  />
                ))}

                {(appointmentsByColumn[column.key] ?? []).map((appointment) => (
                  <CalendarAppointmentBlock
                    key={appointment.id}
                    appointment={appointment}
                    stylistColour={column.colour}
                    dayStartMinutes={dayStartMinutes}
                    pxPerMinute={pxPerMinute}
                    lane={laneLayouts[column.key]?.get(appointment.id)}
                    rescheduling={reschedulingId === appointment.id}
                    rescheduleEnabled={rescheduleEnabled}
                    compact={false}
                    onDragStart={(item) => setDraggingId(item.id)}
                    onSelect={onSelectAppointment}
                  />
                ))}

                {showNowLine ? (
                  <div
                    className="pointer-events-none absolute left-0 right-0 z-20"
                    style={{ top: nowTop }}
                    aria-hidden
                  >
                    <div className="relative border-t border-[#C45C4A]/70">
                      {columnIndex === 0 ? (
                        <span className="absolute -left-1 -top-2.5 rounded-full bg-[#C45C4A] px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-white shadow-sm">
                          {formatMinutesLabel(nowMinutes)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
